/**
 * useDemoAccess
 *
 * Resolves the program-level demo feature toggle.
 *
 * Returns `isDemoAllowed = isDemoModeEnabled() && program.isDemoEnabled === true`.
 *
 * Usage:
 *   const { isDemoAllowed } = useDemoAccess(programId);
 *   if (!isDemoAllowed) return null;
 *
 * The programId argument is optional — when omitted, the hook resolves the
 * id from the route (`:programId`). On every program change the hook also
 * pushes the resolved flag into a module-level cache so non-React consumers
 * (e.g. DemoNarrationService) can read the same value via `isDemoAllowed()`.
 */

import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { programService } from '@/modules/programs/services/programService';
import {
  isDemoModeEnabled,
  setProgramDemoEnabled,
} from '@/modules/shared/services/demoFlowService';

export interface DemoAccessState {
  /** True only when env flag AND program flag are both true. */
  isDemoAllowed: boolean;
  /** True while the program record is loading. */
  isLoading: boolean;
  /** Resolved program id (from arg, route, or URL pathname). */
  programId: string | null;
}

const PROGRAM_ID_RE = /\/programs\/([0-9a-fA-F-]{36})/;

export function useDemoAccess(programIdArg?: string | null): DemoAccessState {
  const params = useParams<{ programId?: string }>();
  const location = useLocation();

  const programId = useMemo(() => {
    if (programIdArg) return programIdArg;
    if (params.programId) return params.programId;
    const match = location.pathname.match(PROGRAM_ID_RE);
    return match?.[1] ?? null;
  }, [programIdArg, params.programId, location.pathname]);

  const envEnabled = isDemoModeEnabled();

  const { data, isLoading } = useQuery({
    queryKey: ['program', programId],
    queryFn: () => programService.getProgramById(programId as string),
    enabled: Boolean(envEnabled && programId),
    staleTime: 60_000,
  });

  const isDemoAllowed = useMemo(() => {
    if (!envEnabled || !programId) return false;
    return data?.data?.isDemoEnabled === true;
  }, [envEnabled, programId, data]);

  // Mirror into the runtime cache so non-React services see the same value.
  useEffect(() => {
    if (!programId) {
      setProgramDemoEnabled(null, false);
      return;
    }
    setProgramDemoEnabled(programId, isDemoAllowed);
  }, [programId, isDemoAllowed]);

  return { isDemoAllowed, isLoading, programId };
}
